import { useCallback, useRef, useState } from "react"
import { AxiosResponse } from "axios"
import { JournalExportFormat } from "@/shared/api/admin-audit-log"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { downloadAttachment, resolveBlobErrorId } from "@/shared/lib/download"

export interface JournalExportButtonsProps {
    /** Запрос экспорта. Вызывается с текущими фильтрами страницы. */
    onExport: (format: JournalExportFormat) => Promise<AxiosResponse<Blob>>
    /** Имя на случай, когда сервер не отдал `Content-Disposition`. */
    fallbackFileName: (format: JournalExportFormat) => string
    /**
     * Фильтры на форме ещё не применены. Пока это так, выгрузка ушла бы по
     * прошлой выборке — администратор получил бы не то, что видит на экране,
     * поэтому кнопки блокируются и состояние объясняется текстом.
     */
    filtersPending?: boolean
}

interface FormatButton {
    format: JournalExportFormat
    label: string
}

// Подписи заданы контрактом и не локализуются динамически.
const FORMAT_BUTTONS: FormatButton[] = [
    { format: "xlsx", label: "Экспорт в XLSX" },
    { format: "csv", label: "Экспорт в CSV" },
]

type FormatState = Record<JournalExportFormat, boolean>
type FormatError = Record<JournalExportFormat, string | null>

const NO_LOADING: FormatState = { csv: false, xlsx: false }
const NO_ERROR: FormatError = { csv: null, xlsx: null }

/**
 * Две независимые кнопки экспорта. Каждая несёт собственные loading/error, не
 * трогает состояние таблицы и скачивает файл ТОЛЬКО для успешного ответа:
 * при ошибке пользователь получает документированное сообщение и ни частичного,
 * ни пустого файла.
 */
export const JournalExportButtons = ({
    onExport,
    fallbackFileName,
    filtersPending = false,
}: JournalExportButtonsProps) => {
    const [loading, setLoading] = useState<FormatState>(NO_LOADING)
    const [errors, setErrors] = useState<FormatError>(NO_ERROR)
    const requestIds = useRef<Record<JournalExportFormat, number>>({ csv: 0, xlsx: 0 })

    const handleExport = useCallback(async (format: JournalExportFormat) => {
        const requestId = ++requestIds.current[format]

        setLoading((current) => ({ ...current, [format]: true }))
        setErrors((current) => ({ ...current, [format]: null }))

        try {
            const response = await onExport(format)
            if (requestIds.current[format] !== requestId) return

            downloadAttachment(response, fallbackFileName(format))
        } catch (error) {
            const errorId = await resolveBlobErrorId(error)
            if (requestIds.current[format] !== requestId) return

            setErrors((current) => ({ ...current, [format]: getErrorMessage(errorId) }))
        } finally {
            if (requestIds.current[format] === requestId) {
                setLoading((current) => ({ ...current, [format]: false }))
            }
        }
    }, [fallbackFileName, onExport])

    return (
        <div className="mb-4 flex flex-col items-end gap-2">
            <div className="flex flex-wrap justify-end gap-3">
                {FORMAT_BUTTONS.map(({ format, label }) => (
                    <button
                        key={format}
                        type="button"
                        onClick={() => handleExport(format)}
                        disabled={loading[format] || filtersPending}
                        aria-busy={loading[format]}
                        // Видимый текст на время загрузки меняется, поэтому
                        // доступное имя держим стабильным: иначе две кнопки
                        // в загрузке звучали бы одинаково.
                        aria-label={label}
                        className="form_handle_button !h-10 !w-auto px-5 disabled:opacity-60"
                    >
                        {loading[format] ? "Готовим файл…" : label}
                    </button>
                ))}
            </div>
            {filtersPending && (
                <span className="text-right text-sm text-[#828282]">
                    Применяем фильтры…
                </span>
            )}
            {FORMAT_BUTTONS.map(({ format, label }) => errors[format] && (
                <span key={format} role="alert" className="text-right text-sm text-color-danger">
                    {label}: {errors[format]}
                </span>
            ))}
        </div>
    )
}
